import { writable, get } from 'svelte/store';
import { jsonHeaders, readApiErrorMessage } from '../apiHelpers';
import { optimisticMutation } from '../optimisticMutation';
import { DEFAULT_MOBILE_TABS, normalizeMobileTabs, type MobileTabKey } from '../mobileTabs';

/** The user's 4 configurable bottom tabs (the fixed More tab is not included). */
export const mobileTabs = writable<MobileTabKey[]>([...DEFAULT_MOBILE_TABS]);

/** Initialize from the `preferences.mobileTabs` Inertia shared prop. */
export function initMobileTabs(tabs: unknown) {
  mobileTabs.set(normalizeMobileTabs(tabs));
}

async function patchMobileTabs(tabs: MobileTabKey[]): Promise<{ mobileTabs: MobileTabKey[] }> {
  const response = await fetch('/preferences/mobile_tabs', {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ mobile_tabs: tabs }),
  });
  if (!response.ok) throw new Error(await readApiErrorMessage(response));
  return response.json();
}

/** Optimistically update the tab bar, then persist. Rolls back and throws on failure. */
export async function saveMobileTabs(tabs: MobileTabKey[]): Promise<void> {
  const result = await optimisticMutation({
    getState: () => get(mobileTabs),
    applyOptimistic: () => [...tabs],
    syncState: (next) => mobileTabs.set(next),
    apiCall: () => patchMobileTabs(tabs),
    onSuccess: (_, apiResult) => normalizeMobileTabs(apiResult.mobileTabs),
  });
  if (!result.success) throw result.cause;
}
